<template>
  <div
    class="product-recommendations container critical-component-clear"
    :class="{
      [$class('loading')]: state === 'loading',
      ['product-recommendations--padding-top']: paddings?.top,
      ['product-recommendations--padding-bottom']: paddings?.bottom,
      [theme]: true
    }"
  >
    <div
      v-if="subtitle"
      class="product-recommendations__subtitle text-body-3xs"
      v-html="subtitle"
    />

    <h2
      :id="`section-title-${liquid.sectionId}`"
      class="
        product-recommendations__title
        text-body-s-desktop text-body-2xs
      "
      :style="`font-family: ${fontStyle}`"
      v-text="title"
    />

    <product-carousel
      :autoplay-speed="autoplaySpeed"
      :namespace="liquid.sectionId"
      :product-card-image-order="productCardImageOrder"
      :product-card-use-alt-image="productCardUseAltImage"
      :products="products"
      @product-card-clicked="sendClickEvent"
    />

    <btn
      v-if="liquid.ctaText && liquid.ctaUrl"
      block-class="product-recommendations"
      :label="liquid.ctaText"
      :url="liquid.ctaUrl"
      :show-icon="false"
      :modifiers="['text']"
      type="button"
    />
  </div>
</template>

<script>

/**
 * Vue: Product recommendations (product-recommendations)
 * -----------------------------------------------------------------------------
 * Section displaying a carousel of recommended products.
 *
 * @param {Object} [containerRef] - Component container reference.
 * @param {String} autoplaySpeed - String representing an autoplay speed in s.
 * @param {Array} handles - Array of product handles from settings.
 * @param {String} liquid.sectionId - Section's Liquid ID.
 * @param {String} liquid.ctaText - CTA text.
 * @param {String} liquid.ctaUrl - CTA url.
 * @param {Object} recommendations - Recommendations settings.
 * @param {Object} paddings - Object containing booleans for top and bottom padding.
 * @param {String} title - String title for the section.
 * @param {String} subtitle - String subtitle for the section.
 * @param {String} fontStyle - String text font style of the title.
 *
 */
import { mapState } from 'vuex'
import ProductCarousel from "~async/product-carousel/product-carousel"

import productQuery from "~/graphql/queries/product.gql"
import { formatProduct } from "~/helpers/format"
import Btn from '~global/btn/btn'

import { formatAlgoliaProduct } from '~/helpers/format-algolia'

import aa from "search-insights"
import algoliaRecommend from '@algolia/recommend'

export default {
  name: "ProductRecommendations",

  components: {
    ProductCarousel,
    Btn,
  },

  props: {
    containerRef: {
      type: Object,
      default: () => ({}),
    },
    autoplaySpeed: {
      type: Number,
      default: 2.5,
    },
    handles: {
      type: Array,
      default: () => [],
      required: true,
    },
    liquid: {
      type: Object,
      default: () => ({
        sectionId: '',
        ctaText: '',
        ctaUrl: '',
      }),
    },
    paddings: {
      type: Object,
      default: () => {},
    },
    recommendations: {
      type: Object,
      default: () => {},
    },
    productCardImageOrder: {
      type: String,
      default: '',
    },
    productCardUseAltImage: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: '',
    },
    subtitle: {
      type: String,
      default: '',
    },
    fontStyle: {
      type: String,
      default: '',
    },
    theme: {
      type: String,
      default: "",
    },
  },

  data() {
    return {
      products: [
        false, false, false, false,
      ],
      recommendationProducts: [],
      queue: [],
      state: "loading",
      algolia: {
        indexProducts: this.$variable('algolia.indexes.products'),
        defaultFilters: this.$variable('algolia.defaultFilters'),
        recommendClient: algoliaRecommend(
          this.$variable('algolia.credentials.appId'),
          this.$variable('algolia.credentials.apiKey'),
        ),
      },
      queryID: null,
    }
  },

  computed: {


    /**
     * Map Vuex state.
     */
     ...mapState({
      cart: (state) => state.cart.response,
      product: (state) => state.product.product,
    }),

    /**
     * Compute cart handles.
     * @returns {Array}
     */
    cartHandles() {
      return this.isCart
        ? this.cart.items.map((item) => item.handle)
        : []
    },

    /**
     * Compute if on cart page.
     * @returns {Boolean}
     */
    isCart() {
      return cnvs.page.type === 'cart'
    },
  },

  watch: {
    product: {
      immediate: true,
      deep: true,
      handler() {
        if(!this.product?.id) {
          return
        }

        this.recommendationProducts = [`${this.product.id}`]
      },
    },
    recommendationProducts() {

      /**
       * Reinitialize component when pdp product changed dynamically.
       */
      this.init()
    },
  },

  mounted() {
    this.recommendationProducts = this.recommendations.productIds
    this.init()
  },

  methods: {

    async init() {
      if (!this.recommendations.enabled && !this.handles?.length) {
        this.hideComponent()
        return
      }

      this.containerRef.classList?.remove(this.$class('hidden'))
      this.state = 'loading'

      /**
       * If recommendations aren't enabled or its recommendationProducts array is empty then
       * load fallback products.
       */
      if (
        !this.recommendations.enabled ||
        (!this.recommendationProducts.length && this.recommendations.type !== 'trending-items')
      ) {
        this.queryProducts()
        return
      }

      const products = await this.getRecommendedProducts()

      if (products?.length) {
        this.products = products
        this.state = 'ready'
        return
      }

      // Hide component.
      this.hideComponent()
    },

     /**
     * Get active block placement products if they exist.
     * @returns {Boolean|Array}
     */
    async getRecommendedProducts() {
      if (!this.recommendations?.enabled) {
        return []
      }

      const client = this.algolia.recommendClient
      let recommendationsArray = []

      if(this.recommendations.type !== 'trending-items') {
        recommendationsArray = this.recommendationProducts.map(productId => {
          const recommendationObject = {
            indexName: this.algolia.indexProducts,
            model: this.recommendations.type,
            threshold: this.recommendations.threshold,
            // Fetch more products than the max products setting to account for filtering out products already in the cart
            maxRecommendations: 20,
            queryParameters: {
              clickAnalytics: true,
            },
          }

          recommendationObject.objectID = productId

          if (this.recommendations?.rules.length) {
            recommendationObject.queryParameters.ruleContexts = this.recommendations.rules
          }


          recommendationObject.queryParameters.filters = this.recommendations.queryFilters ?
            `${this.recommendations.queryFilters} AND ${this.algolia.defaultFilters}` :
            `${this.algolia.defaultFilters}`

          if (this.recommendations.fallbackFilters) {
            recommendationObject.fallbackParameters = {
              filters: this.recommendations.fallbackFilters,
            }
          }

          return recommendationObject
        })
      } else {
        const recommendationObject = {
            indexName: this.algolia.indexProducts,
            model: this.recommendations.type,
            threshold: this.recommendations.threshold,
            // Fetch more products than the max products setting to account for filtering out products already in the cart
            maxRecommendations: 20,
            queryParameters: {
              clickAnalytics: true,
            },
          }

          if (this.recommendations?.rules.length) {
            recommendationObject.queryParameters.ruleContexts = this.recommendations.rules
          }


          recommendationObject.queryParameters.filters = this.recommendations.queryFilters ?
            `${this.recommendations.queryFilters} AND ${this.algolia.defaultFilters}` :
            `${this.algolia.defaultFilters}`

          if (this.recommendations.fallbackFilters) {
            recommendationObject.fallbackParameters = {
              filters: this.recommendations.fallbackFilters,
            }
          }

          recommendationsArray = [recommendationObject]
      }

      if(!recommendationsArray.length) {
        return []
      }

      const responses = await client.getRecommendations(recommendationsArray)

      const recommendationArrays = responses.results.map((response) => {
        if (!response.hits || !response.hits.length) {
          return false
        }

        if(response.queryID) {
          this.queryID = response.queryID
        }

        return [...response.hits]
      }).filter(Boolean)

      return this.flattenRecommendationArrays(recommendationArrays)
    },

    /**
     * Flatten recommendations array.
     * @param {Array} arrays - Array of recommendations array for each product.
     * @returns {Array}
     */
    flattenRecommendationArrays(arrays) {
      const recommendations = []
      let index = 0

      /**
       * Go through each recommendation array pulling a product from each until
       * the product count is reached or recommendation arrays are empty.
       */
      while (recommendations.length < this.recommendations.max && arrays.length) {
        arrays.forEach((array, arrayIndex) => {
          if (!array[index]) {
            arrays.splice(arrayIndex, 1)
            return
          }

          const alreadyExists = recommendations.find((product) => {
            return product.handle === array[index].handle
          })

          if (alreadyExists || this.cartHandles.includes(array[index].handle)) {
            return
          }

          recommendations.push(formatAlgoliaProduct({
            ...array[index],
            __position: index,
            __queryID: this.queryID,
          }))
        })

        index++
      }

      return recommendations
    },

    /**
     * Query products from GraphQL API.
     */
    async queryProducts() {
      try {
        this.queue = this.handles.map((handle) => {
          return cnvs.Query({
            query: productQuery,
            variables: {
              country: cnvs.store.country,
              handle,
              language: cnvs.store.language,
            },
          })
        })

        const response = await Promise.allSettled(this.queue)
        const products = response
          .map((promise) => {
            if (promise.status !== "fulfilled" || !promise.value?.product) {
              return false
            }

            return formatProduct(promise.value)
          })
          .filter(Boolean)

        /**
         * If no products returned then error.
         */
        if (!products.length) {
          this.state = "error"
          return
        }

        this.products = products
        this.state = "ready"
      } catch (error) {
        this.state = "error"
        throw new Error(error)
      }
    },

    /**
     * Hide component when it's empty.
     */
    hideComponent() {
      this.containerRef.classList?.add(this.$class('hidden'))
    },

    /**
     * Send click event.
     * @param {Object} product - Product object.
     */
    sendClickEvent(product) {

      /**
       * If recommendations aren't enabled or its recommendationProducts array is empty then
       * algolia recommendations are not used, so we don't need to send events.
       */
      if (
        !this.recommendations.enabled ||
        (!this.recommendationProducts.length && this.recommendations.type !== 'trending-items')
      ) {
        return
      }

      this.queryID ?
        aa('clickedObjectIDsAfterSearch', {
          index: this.$variable('algolia.indexes.products'),
          eventName: 'Product Clicked',
          objectIDs: [product.id.toString()],
          queryID: this.queryID,
          positions: [product['__position'] || 0],
        }) :
        aa('clickedObjectIDs', {
          index: this.$variable('algolia.indexes.products'),
          eventName: 'Product Clicked',
          objectIDs: [product.id.toString()],
          positions: [product['__position'] || 0],
        })
    },
  },
}
</script>

<style lang="scss">
@import "./product-recommendations";
</style>
